import React, { useEffect, useMemo, useState } from 'react'
import Breadcrumb from '../../components/common/Breadcrumb'
import ReactDataTable from 'react-data-table-component';
import {
    IconEdit, IconTrash, IconLoader2,
    IconChevronRight, IconChevronLeft, IconEye, IconEyeOff
} from '@tabler/icons-react';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useRoles } from '../../hooks/useRoles';
import { useScopeTypes } from '../../hooks/useScopeTypes';
import { useUsers, useCreateUser, useDeleteUser, useUpdateUser } from '../../hooks/useUsers';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import ActionDropdown from '../../components/common/ActionDropdown';

import {
    Grid, List, ListItem, ListItemIcon, ListItemText,
    Checkbox, Button, Paper, Typography, Divider, StyledEngineProvider
} from '@mui/material';

export default function User() {
    const { user } = useAuth();
    const [addUserModal, setAddUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [selectedOrgId, setSelectedOrgId] = useState(user?.orgId || '');
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        confirm_password: '',
        role_id: '',
        org_id: ''
    });

    const [checked, setChecked] = useState([]);
    const [left, setLeft] = useState([]);
    const [right, setRight] = useState([]);
    const [permSearch, setPermSearch] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const { data: orgsData, isLoading: orgsLoading } = useOrganizations();
    const organizations = orgsData?.results || orgsData || [];

    const { data: rolesData, isLoading: rolesLoading } = useRoles(selectedOrgId);
    const roles = rolesData?.results || rolesData || [];

    const { data: usersData, isLoading: usersLoading } = useUsers(selectedOrgId);
    const users = usersData?.results || usersData || [];

    const { data: allPermissions, isLoading: permsLoading } = usePermissions();

    const createUserMutation = useCreateUser();
    const updateUserMutation = useUpdateUser();
    const deleteUserMutation = useDeleteUser();

    // This to initialize permissions when modal opens
    useEffect(() => {
        if (addUserModal && allPermissions) {
            setLeft(allPermissions);
            setRight([]);
            setChecked([]);
            setPermSearch('');
        }
    }, [addUserModal, allPermissions]);

    // When role is selected, populate permissions from that role
    useEffect(() => {
        if (formData.role_id && allPermissions && allPermissions.length > 0) {
            const selectedRole = roles.find(r => r.id === formData.role_id);
            if (selectedRole?.permissions && selectedRole.permissions.length > 0) {
                // Role permissions are codenames like "rbacapp.add_orgrole"
                // Match them with allPermissions by codename
                const assignedPerms = selectedRole.permissions
                    .map(codename => {
                        // Try exact match first, then try without app prefix
                        return allPermissions.find(p =>
                            p.codename === codename ||
                            `rbacapp.${p.codename}` === codename ||
                            codename.endsWith(`.${p.codename}`)
                        );
                    })
                    .filter(Boolean);

                const availablePerms = allPermissions.filter(p =>
                    !assignedPerms.some(ap => ap.id === p.id)
                );

                setRight(assignedPerms);
                setLeft(availablePerms);
                setChecked([]);
            } else {
                // No permissions on role, reset to all available
                setLeft(allPermissions);
                setRight([]);
            }
        }
    }, [formData.role_id, roles, allPermissions]);

    useEffect(() => {
        document.body.classList[addUserModal ? "add" : "remove"]("overflow-hidden");
    }, [addUserModal]);

    const intersection = (a, b) => a.filter((value) => b.indexOf(value) !== -1);
    const not = (a, b) => a.filter((value) => b.indexOf(value) === -1);

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];
        if (currentIndex === -1) newChecked.push(value);
        else newChecked.splice(currentIndex, 1);
        setChecked(newChecked);
    };

    const handleCheckedRight = () => {
        setRight(right.concat(leftChecked));
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked));
        setRight(not(right, rightChecked));
        setChecked(not(checked, rightChecked));
    };

    const renderPermissionList = (title, items) => {
        const filtered = items.filter(i =>
            (i.name || "").toLowerCase().includes(permSearch.toLowerCase()) ||
            (i.codename || "").toLowerCase().includes(permSearch.toLowerCase())
        );

        return (
            <Paper className="border border-border bg-bg-light" sx={{ width: '100%', height: 280, display: 'flex', flexDirection: 'column', boxShadow: 'none' }}>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 'bold', display: 'block', bgcolor: 'rgba(0,0,0,0.05)', color: 'var(--text-main)' }}>
                    {title} ({items.length})
                </Typography>
                <Divider />
                <List dense sx={{ flex: 1, overflow: 'auto' }} className="cus-scrollbar">
                    {filtered.map((value) => (
                        <ListItem key={value.id} button onClick={handleToggle(value)} sx={{ px: 1 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Checkbox checked={checked.indexOf(value) !== -1} size="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-main)' }}>{value.name}</Typography>}
                                secondary={<Typography sx={{ fontSize: '10px', color: 'gray' }}>{value.codename}</Typography>}
                            />
                        </ListItem>
                    ))}
                </List>
            </Paper>
        );
    };

    const openAddUserModal = () => {
        setAddUserModal(true);
        setEditingUser(null);
        setFormData({ username: '', email: '', first_name: '', last_name: '', password: '', confirm_password: '', role_id: '', org_id: selectedOrgId || '' });
        setShowPassword(false);
        setShowConfirmPassword(false);
        setRight([]);
        setLeft(allPermissions || []);
    };

    const handleEdit = (userToEdit) => {
        setEditingUser(userToEdit);
        setFormData({
            username: userToEdit.username,
            email: userToEdit.email,
            role_id: userToEdit.roleDetails?.id || userToEdit.role || '',
            org_id: userToEdit.organization?.id || selectedOrgId
        });

        // Populate permissions
        // Assuming userToEdit.permissions contains list of permission strings (codenames)
        // We match them to allPermissions to populate 'right'
        if (allPermissions) {
            const userPerms = userToEdit.permissions || [];
            const assigned = allPermissions.filter(p => userPerms.includes(p.codename) || userPerms.includes(`rbacapp.${p.codename}`));
            const unassigned = allPermissions.filter(p => !assigned.find(a => a.id === p.id));
            setRight(assigned);
            setLeft(unassigned);
        } else {
            setRight([]);
            setLeft(allPermissions || []);
        }

        setAddUserModal(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'org_id') setSelectedOrgId(value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingUser) {
                const payload = {
                    data: {
                        ...formData,
                        user_permissions: right.map(p => p.id)
                    },
                    orgId: formData.org_id
                };
                const result = await updateUserMutation.mutateAsync({ id: editingUser.id, ...payload });
                const name = result.user?.username || result.username || formData.username;
                toast.success(`User "${name}" updated successfully`);
            } else {
                const createPayload = {
                    data: {
                        username: formData.username,
                        email: formData.email,
                        first_name: formData.first_name,
                        last_name: formData.last_name,
                        password: formData.password,
                        confirm_password: formData.confirm_password,
                        org_id: formData.org_id,
                        role_id: formData.role_id,
                    },
                    orgId: formData.org_id
                };
                const result = await createUserMutation.mutateAsync(createPayload);
                const name = result.user?.username || result.username || formData.username;
                toast.success(`User "${name}" created successfully`);
            }
            setAddUserModal(false);
        } catch (error) {
            console.error('Error saving user:', error);
            // Error handled by interceptor
        }
    };

    const handleDelete = (row) => {
        setItemToDelete(row);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await deleteUserMutation.mutateAsync({ id: itemToDelete.id, orgId: selectedOrgId, confirm: true });
            toast.success(`User "${itemToDelete.username}" deleted successfully`);
            setDeleteModalOpen(false);
            setItemToDelete(null);
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const columnsFilter = [
        {
            name: 'Avatar',
            width: "80px",
            cell: (row) => {
                const colors = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4'];
                const name = row.first_name && row.last_name
                    ? `${row.first_name} ${row.last_name}`
                    : row.username || '';
                const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
                const bg = colors[(name.charCodeAt(0) || 0) % colors.length];
                return (
                    <div style={{ backgroundColor: bg }} className="w-[35px] h-[35px] rounded-full flex items-center justify-center text-white font-semibold text-xs">
                        {initials}
                    </div>
                );
            },
        },
        { name: 'Username', selector: row => row.username, sortable: true },
        { name: 'Email', selector: row => row.email, sortable: true },
        {
            name: 'Role',
            selector: row => row.roles?.map(r => r.name).join(', ') || 'N/A',
            sortable: true
        },
        {
            name: 'Organization',
            selector: row => row.roles?.map(r => r.org_name).filter(Boolean).join(', ') || 'N/A',
            sortable: true
        },
        {
            name: 'Action',
            cell: row => (
                <div className="flex justify-center">
                    <ActionDropdown
                        menuItems={[
                            {
                                label: 'Edit / Reassign',
                                icon: IconEdit,
                                onClick: () => handleEdit(row),
                                description: "Change roles or permissions"
                            },
                            {
                                label: 'Delete User',
                                icon: IconTrash,
                                danger: true,
                                onClick: () => handleDelete(row),
                                description: "Permanently remove account"
                            }
                        ]}
                    />
                </div>
            ),
            width: "120px",
            center: true
        }
    ];

    const [filterText, setFilterText] = useState('');
    const filteredItems = users.filter(item =>
        (item?.username?.toLowerCase().includes(filterText.toLowerCase())) ||
        (item?.email?.toLowerCase().includes(filterText.toLowerCase()))
    );

    const subHeaderComponentMemo = useMemo(() => (
        <div className='flex gap-2 mb-4 justify-end w-full'>
            <input
                type='text'
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                placeholder='Search users...'
                className='bg-bg-light border border-border py-1 px-3 rounded focus:outline-none text-sm'
            />
        </div>
    ), [filterText]);

    const isSaving = createUserMutation.isPending;

    return (
        <StyledEngineProvider injectFirst>
            <Breadcrumb title="Users" path="Users" />
            <div className='bg-bg-dark rounded-lg shadow-light md:p-20 p-15'>
                <div className='flex flex-wrap items-center justify-between gap-15 mb-20'>
                    <p className='text-[16px] font-medium'>Users List</p>
                    <div className='flex items-center gap-10'>
                        <select
                            value={selectedOrgId}
                            onChange={(e) => setSelectedOrgId(e.target.value)}
                            className='bg-bg-light border border-border py-1.5 px-3 rounded focus:outline-none text-sm'
                        >
                            <option value=''>Select Organization</option>
                            {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                        </select>
                        <button onClick={openAddUserModal} className='btn btn-primary btn-sm'>Add User</button>
                    </div>
                </div>

                {usersLoading ? (
                    <div className='flex justify-center py-10'><IconLoader2 className='animate-spin' /></div>
                ) : (
                    <div className='react-data-table'>
                        <ReactDataTable
                            columns={columnsFilter}
                            data={filteredItems}
                            pagination
                            subHeader
                            subHeaderComponent={subHeaderComponentMemo}
                        />
                    </div>
                )}
            </div>

            {addUserModal && (
                <>
                    <div className="fixed p-15 w-full max-w-[850px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[1001]">
                        <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-xl border border-border'>
                            <h5 className="text-[16px] font-medium py-10 mb-20 text-center border-b border-border">
                                {editingUser ? 'Edit User' : 'Add New User'}
                            </h5>
                            <form onSubmit={handleSubmit}>
                                <div className='my-10 md:px-10 px-[7px] max-h-[75vh] overflow-auto cus-scrollbar'>
                                    <div className='grid grid-cols-12 gap-x-20 gap-y-10'>

                                        <div className='col-span-12 grid grid-cols-2 gap-15'>
                                            <div className='flex flex-col'>
                                                <label className='text-xs font-medium mb-1'>Organization *</label>
                                                <select name='org_id' value={formData.org_id} onChange={handleChange} required className='bg-bg-light border border-border p-2 rounded text-sm'>
                                                    <option value=''>Select Organization</option>
                                                    {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                                                </select>
                                            </div>
                                            <div className='flex flex-col'>
                                                <label className='text-xs font-medium mb-1'>Role *</label>
                                                <select name='role_id' value={formData.role_id} onChange={handleChange} required className='bg-bg-light border border-border p-2 rounded text-sm'>
                                                    <option value=''>Select Role</option>
                                                    {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
                                                </select>
                                            </div>
                                        </div>

                                        <div className='col-span-6 flex flex-col'>
                                            <label className='text-xs font-medium mb-1'>First Name *</label>
                                            <input type='text' name='first_name' value={formData.first_name} onChange={handleChange} required className='bg-bg-light border border-border p-2 rounded text-sm' />
                                        </div>

                                        <div className='col-span-6 flex flex-col'>
                                            <label className='text-xs font-medium mb-1'>Last Name *</label>
                                            <input type='text' name='last_name' value={formData.last_name} onChange={handleChange} required className='bg-bg-light border border-border p-2 rounded text-sm' />
                                        </div>

                                        <div className='col-span-6 flex flex-col'>
                                            <label className='text-xs font-medium mb-1'>Username *</label>
                                            <input type='text' name='username' value={formData.username} onChange={handleChange} required autoComplete="off" className='bg-bg-light border border-border p-2 rounded text-sm' />
                                        </div>

                                        <div className='col-span-6 flex flex-col'>
                                            <label className='text-xs font-medium mb-1'>Email *</label>
                                            <input type='email' name='email' value={formData.email} onChange={handleChange} required className='bg-bg-light border border-border p-2 rounded text-sm' />
                                        </div>

                                        {!editingUser && (
                                            <>
                                                <div className='col-span-6 flex flex-col'>
                                                    <label className='text-xs font-medium mb-1'>Password *</label>
                                                    <div className='relative'>
                                                        <input type={showPassword ? 'text' : 'password'} name='password' value={formData.password} onChange={handleChange} required autoComplete="new-password" className='bg-bg-light border border-border p-2 pr-9 rounded text-sm w-full' />
                                                        <button type='button' onClick={() => setShowPassword(p => !p)} className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'>
                                                            {showPassword ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className='col-span-6 flex flex-col'>
                                                    <label className='text-xs font-medium mb-1'>Confirm Password *</label>
                                                    <div className='relative'>
                                                        <input type={showConfirmPassword ? 'text' : 'password'} name='confirm_password' value={formData.confirm_password} onChange={handleChange} required autoComplete="new-password" className='bg-bg-light border border-border p-2 pr-9 rounded text-sm w-full' />
                                                        <button type='button' onClick={() => setShowConfirmPassword(p => !p)} className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'>
                                                            {showConfirmPassword ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        <div className='col-span-12 mt-10 border-t border-border pt-15'>
                                            <div className='flex justify-between items-center mb-10'>
                                                <input
                                                    type="text"
                                                    placeholder="Filter permissions..."
                                                    className="bg-bg-light border border-border px-3 py-1.5 rounded text-xs w-64 focus:outline-none focus:border-primary"
                                                    onChange={(e) => setPermSearch(e.target.value)}
                                                />
                                            </div>

                                            <div className="flex items-center justify-between gap-4">
                                                <div className="flex-1">
                                                    {renderPermissionList('Available', left)}
                                                </div>

                                                <div className="flex flex-col gap-2 px-2">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={handleCheckedRight}
                                                        disabled={leftChecked.length === 0}
                                                        sx={{
                                                            minWidth: 40,
                                                            height: 40,
                                                            borderColor: 'var(--border)',
                                                            color: 'var(--text-main)',
                                                            '&:hover': { borderColor: 'var(--primary)' }
                                                        }}
                                                    >
                                                        <IconChevronRight size={20} />
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={handleCheckedLeft}
                                                        disabled={rightChecked.length === 0}
                                                        sx={{
                                                            minWidth: 40,
                                                            height: 40,
                                                            borderColor: 'var(--border)',
                                                            color: 'var(--text-main)',
                                                            '&:hover': { borderColor: 'var(--primary)' }
                                                        }}
                                                    >
                                                        <IconChevronLeft size={20} />
                                                    </Button>
                                                </div>

                                                <div className="flex-1">
                                                    {renderPermissionList('Assigned', right)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='flex gap-10 justify-end p-10 border-t border-border mt-10'>
                                    <button type='button' onClick={() => setAddUserModal(false)} className='btn btn-outline-secondary btn-sm'>Cancel</button>
                                    <button type='submit' className='btn btn-primary btn-sm' disabled={isSaving || updateUserMutation.isPending || permsLoading}>
                                        {isSaving || updateUserMutation.isPending ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div onClick={openAddUserModal} className='fixed z-[1000] w-full h-full left-0 top-0 bg-black/50 backdrop-blur-[2px]'></div>
                </>
            )}

            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => { setDeleteModalOpen(false); setItemToDelete(null); }}
                onConfirm={confirmDelete}
                title="Delete User"
                message="This will permanently delete the user account. Note: Employee records (if any) will NOT be deleted."
                itemName={itemToDelete?.username}
                isLoading={deleteUserMutation.isPending}
            />
        </StyledEngineProvider>
    )
}